import React, { useEffect } from 'react';
import { useContext, useState } from 'react';
import { IUser } from './userInterfaces';
import {
  CanAccessUserData,
  IsSelectableClaim,
  IsVytalEmployeeClaim,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { adminUserQuery } from '../../graphql/queries';
import { useQuery } from '@apollo/client';
import ClassicLoadingSpinner from '../../components/LoadingSpinner/ClassicLoadingSpinner';
import i18n from '@/utils/i18n';
import { SelectClaimModal } from './SelectClaimModal';
import { LayoutStoreInstance } from '../../LayoutStore';
import { useToggleTapToBorrowMutation } from '../../graphql/mutations/toggleTapToBorrowEnabled.generated';
import { useLinkUserToContainerStationMutation } from '../../graphql/mutations/linkUserToContainerStation.generated';
import { useToggleNextHandoutMutation } from '../../graphql/mutations/toggleNextHandoutEnabled.generated';
import { IdName } from '../../components/EditMerchantOrder/HandleStatusChange';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { CustomModal } from '../../components/CustomModal';
import StoreSearch from '../../components/StoreSearch';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import { FaSave } from 'react-icons/fa';
import { useLinkCustomerCateringUserToStoreMutation } from '../../graphql/mutations/linkCustomerCateringUserToStore.generated';
import { useSetTapToBorrowTypeMutation } from '../../graphql/mutations/setUserTapToBorrowType.generated';
import { useSetUserStripeConnectedAccountMutation } from '../../graphql/mutations/setUserStripeConnectedAccount.generated';
import { useGetStripeTerminalLocationMutation } from '../../graphql/mutations/getStripeTerminalLocation.generated';
import { useStoreLazyQuery } from '../../graphql/queries/storeAdminInfo.generated';
import { useGetTapToBorrowSupportByCountryQuery } from '../../graphql/queries/getTapToBorrowSupportByCountry.generated';
import { TapToBorrowType } from '../../graphql/generatedTypes';
import { Role } from '../../contants';

export type ReducedStore = {
  textId: string;
  name: string;
  brandTextId: string;
};

type UserDetailProps = {
  user: IUser | null;
  userClaim: string | null;
  linkedStore: ReducedStore | null;
  firstCheckoutStore: ReducedStore | null;
  oneTimeToken: string | null;
  toggleIsChargeableForOverdue: Function;
  toggleIsInternal: Function;
  toggleShowSetCheckoutLimitModal: Function;
  linkedStoreContainerStations: IdName[] | null;
  reloadUser: Function;
};

// Generic Enum Value Interface
interface EnumValue {
  name: string; // Allows any string-based enum value
}

// Generic Enum Info Interface
interface EnumInfo {
  name: string; // The name of the enum
  enumValues: EnumValue[]; // List of possible values
}

type TapToBorrowTypeExpanded =
  | TapToBorrowType
  | 'STRIPE_SETUP_INTENT_INCL_PI_FALLBACK';

const stripeSetupIntentInclPiFallback = 'STRIPE_SETUP_INTENT_INCL_PI_FALLBACK';

export default function UserDetailTab(props: UserDetailProps) {
  const {
    user,
    userClaim: userClaimIn,
    firstCheckoutStore,
    oneTimeToken,
    toggleIsChargeableForOverdue,
    toggleIsInternal,
    toggleShowSetCheckoutLimitModal,
    linkedStoreContainerStations,
    reloadUser,
  } = props;
  const viewer = useContext(ViewerContext);
  const [type, setType] = useState<string | null>(null);
  const [tapToBorrowEnabled, setTapToBorrowEnabled] = useState<boolean>(false);
  const [tapToBorrowTwoTapFlow, setTapToBorrowTwoTapFlow] =
    useState<boolean>(false);
  const [tapToBorrowType, setTapToBorrowType] =
    useState<TapToBorrowTypeExpanded | null>(null);
  // The type as it is stored on the user — tapToBorrowType follows the dropdown, so it
  // cannot be used to judge what the tap2borrow flow actually does right now.
  const [savedTapToBorrowType, setSavedTapToBorrowType] = useState<
    string | null
  >(null);
  const [nextHandoutEnabled, setNextHandoutEnabled] = useState<boolean>(false);
  const [stripeConnectedAccountId, setStripeConnectedAccountId] = useState<
    string | null
  >(null);
  const [connectedAccountInput, setConnectedAccountInput] =
    useState<string>('');
  const [linkedStore, setLinkedStore] = useState<ReducedStore | null>(
    props.linkedStore,
  );
  const [userClaim, setUserClaim] = useState<string | null>(userClaimIn);
  const [selectedStation, setSelectedStation] = useState<string | null>(null);
  const [showSelectLinkedStoreModal, setShowSelectLinkedStoreModal] =
    useState(false);
  const [selectedStoreTextId, setSelectedStoreTextId] = useState<string>('');
  const [selectedStoreName, setSelectedStoreName] = useState<string>('');
  const [selectedStoreBrandId, setSelectedStoreBrandId] = useState<string>('');
  const [showEnableTapToBorrowModal, setShowEnableTapToBorrowModal] =
    useState(false);
  const [colugoStoreId, setColugoStoreId] = useState<string | null>(null);

  const [fetchStoreDetails, { data: storeData, loading: storeLoading }] =
    useStoreLazyQuery();

  const [
    getStripeTerminalLocation,
    {
      data: stripeTerminalLocationData,
      loading: loadingStripeTerminalLocation,
      error: stripeTerminalLocationError,
    },
  ] = useGetStripeTerminalLocationMutation();
  const [stripeTerminalLocationFetched, setStripeTerminalLocationFetched] =
    useState(false);

  const { data: tapToBorrowSupportData } =
    useGetTapToBorrowSupportByCountryQuery();

  useEffect(() => {
    if (colugoStoreId) {
      fetchStoreDetails({ variables: { id: colugoStoreId } });
    }
  }, [colugoStoreId, fetchStoreDetails]);

  useEffect(() => {
    if (userClaimIn) {
      setUserClaim(userClaimIn);
    }
  }, [userClaimIn]);

  const [linkCustomerCateringUser] = useLinkCustomerCateringUserToStoreMutation(
    {
      onError: (e) => {
        LayoutStoreInstance.setError(e.message.replace(/_/g, ' '));
        setSelectedStoreName('');
        setSelectedStoreTextId('');
        setSelectedStoreBrandId('');
      },
      onCompleted: () => {
        LayoutStoreInstance.setSuccess(i18n.t('Users:storeLinkedSuccessfully'));
        setLinkedStore({
          textId: selectedStoreTextId,
          name: selectedStoreName,
          brandTextId: selectedStoreBrandId,
        });
        setSelectedStoreName('');
        setSelectedStoreTextId('');
        setSelectedStoreBrandId('');
      },
    },
  );
  const [
    linkUserToContainerStationModalOpen,
    setLinkUserToContainerStationModalOpen,
  ] = useState(false);
  const { data: userDataWithEnumInfo, loading } = useQuery(adminUserQuery, {
    skip: user?.pickpackId == null,
    variables: { id: user?.pickpackId },
    fetchPolicy: 'no-cache',
    onCompleted: (data: {
      user: {
        account_type: any;
        feature_next_handout_enabled: boolean;
        feature_tap_to_borrow_enabled: boolean;
        feature_tap_to_borrow_two_tap_flow: boolean;
        tap_to_borrow_type: string;
        stripe_connected_account_id: string | null;
        store_id: string | null;
      };
      tapToBorrowEnumInfo: EnumInfo;
    }) => {
      console.log('data', data);
      setType(data.user.account_type);
      setColugoStoreId(data.user.store_id);
      setNextHandoutEnabled(data.user.feature_next_handout_enabled);
      setTapToBorrowEnabled(data.user.feature_tap_to_borrow_enabled);
      setTapToBorrowTwoTapFlow(data.user.feature_tap_to_borrow_two_tap_flow);
      setStripeConnectedAccountId(data.user.stripe_connected_account_id);
      setConnectedAccountInput(data.user.stripe_connected_account_id || '');

      let type = data.user.tap_to_borrow_type;
      let isTwoTapFlow = data.user.feature_tap_to_borrow_two_tap_flow;

      setSavedTapToBorrowType(type);
      setTapToBorrowType(
        (type === TapToBorrowType.StripeSetupIntent && isTwoTapFlow
          ? stripeSetupIntentInclPiFallback
          : type) as TapToBorrowTypeExpanded,
      );
    },
  });
  const tapToBorrowEnumInfo = userDataWithEnumInfo?.tapToBorrowEnumInfo;

  const [toggleFeatureTapToBorrow, { loading: updatingTapToBorrow }] =
    useToggleTapToBorrowMutation();

  const [toggleNextHandout, { loading: updatingNextHandout }] =
    useToggleNextHandoutMutation();

  const [toggleFeatureTapToBorrowType, { loading: updatingTapToBorrowType }] =
    useSetTapToBorrowTypeMutation();

  const [setUserStripeConnectedAccount, { loading: updatingConnectedAccount }] =
    useSetUserStripeConnectedAccountMutation();

  async function saveStripeConnectedAccount() {
    if (user?.pickpackId == null) {
      return LayoutStoreInstance.setError(
        i18n.t('Users:userDoesNotHavePickpackId'),
      );
    }
    await setUserStripeConnectedAccount({
      variables: {
        id: user.pickpackId,
        stripe_connected_account_id: connectedAccountInput.trim(),
      },
      onCompleted: (data) => {
        if (data?.adminUpdateUser != null) {
          const saved =
            data.adminUpdateUser.stripe_connected_account_id || null;
          setStripeConnectedAccountId(saved);
          setConnectedAccountInput(saved || '');
          LayoutStoreInstance.setSuccess(
            saved
              ? 'Stripe connected account linked'
              : 'Stripe connected account removed',
          );
        }
      },
      onError: (e) => {
        LayoutStoreInstance.setError(e.message.replace(/_/g, ' '));
      },
    });
  }

  const linkedContainerStationsOptions = linkedStoreContainerStations?.map(
    (station) => {
      return { value: station.id, label: station.name };
    },
  );

  const [
    linkUserToContainerStation,
    { loading: linkingUserToContainerStation },
  ] = useLinkUserToContainerStationMutation();

  function getLoginType() {
    if (loading) {
      return <ClassicLoadingSpinner />;
    }
    return type != null ? type : i18n.t('Users:notSet');
  }

  function getTapToBorrowEnabled(user: IUser) {
    if (user.pickpackId == null) {
      return i18n.t('Users:userNeedsToLoginForTapToBorrow');
    }
    if (loading || updatingTapToBorrow) {
      return <ClassicLoadingSpinner />;
    }
    return (
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id="tapToBorrowCheckbox"
          checked={tapToBorrowEnabled === true}
          onChange={() => {
            if (!tapToBorrowEnabled) {
              if (colugoStoreId) {
                fetchStoreDetails({ variables: { id: colugoStoreId } });
              }
              setShowEnableTapToBorrowModal(true);
            } else {
              toggleTapToBorrow({
                tapToBorrowEnabled: false,
                tapToBorrowTwoTapFlow,
              });
            }
          }}
        />
        <label className="form-check-label" htmlFor="tapToBorrowCheckbox">
          {tapToBorrowEnabled === true
            ? i18n.t('Users:enabled')
            : i18n.t('Users:disabled')}
        </label>
      </div>
    );
  }

  function getNextHandoutEnabled(user: IUser) {
    if (user.pickpackId == null) {
      return i18n.t('Users:requiresLoginNextHandout');
    }
    if (loading || updatingNextHandout) {
      return <ClassicLoadingSpinner />;
    }
    return (
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id="nextHandoutCheckbox"
          checked={nextHandoutEnabled === true}
          onChange={() =>
            toggleNextHandoutEnabled({ enabled: !nextHandoutEnabled })
          }
        />
        <label className="form-check-label" htmlFor="nextHandoutCheckbox">
          {nextHandoutEnabled === true
            ? i18n.t('Users:enabled')
            : i18n.t('Users:disabled')}
        </label>
      </div>
    );
  }

  async function toggleNextHandoutEnabled(data: { enabled: boolean }) {
    if (user?.pickpackId != null) {
      await toggleNextHandout({
        variables: {
          id: user?.pickpackId || '',
          feature_next_handout_enabled: data.enabled,
        },
        onCompleted: (data) => {
          if (data?.adminUpdateUser != null) {
            setNextHandoutEnabled(
              data.adminUpdateUser.feature_next_handout_enabled,
            );
          }
        },
        onError: (e) => {
          LayoutStoreInstance.setError(e.message.replace(/_/g, ' '));
        },
      });
    } else {
      LayoutStoreInstance.setError('User does not have a pickpack id');
    }
  }

  function linkContainerStationModal() {
    return (
      <Modal
        isOpen={linkUserToContainerStationModalOpen}
        toggle={() =>
          setLinkUserToContainerStationModalOpen(
            !linkUserToContainerStationModalOpen,
          )
        }
      >
        <ModalHeader
          toggle={() =>
            setLinkUserToContainerStationModalOpen(
              !linkUserToContainerStationModalOpen,
            )
          }
        >
          {i18n.t('Users:selectContainerStation')}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="stationSelect">{i18n.t('Users:station')}</Label>
            <Input
              type="select"
              name="stationSelect"
              id="stationSelect"
              value={selectedStation || '-'}
              onChange={(e) => setSelectedStation(e.target.value)}
            >
              {(linkedContainerStationsOptions || []).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={async () => {
              if (selectedStation && user?.id != null) {
                await linkUserToContainerStation({
                  variables: {
                    user_uuid: user?.id,
                    container_station_id: selectedStation,
                  },
                  onCompleted: () => reloadUser(),
                });
                setLinkUserToContainerStationModalOpen(false);
              }
            }}
            disabled={linkingUserToContainerStation}
          >
            {i18n.t('Users:linkContainerStation')}
          </Button>{' '}
          <Button
            color="secondary"
            onClick={() => setLinkUserToContainerStationModalOpen(false)}
          >
            {i18n.t('Users:cancel')}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  async function toggleTapToBorrow(data: {
    tapToBorrowTwoTapFlow: boolean;
    tapToBorrowEnabled: boolean;
  }) {
    if (user?.pickpackId != null) {
      await toggleFeatureTapToBorrow({
        variables: {
          feature_tap_to_borrow_enabled: data.tapToBorrowEnabled,
          feature_tap_to_borrow_two_tap_flow: data.tapToBorrowTwoTapFlow,
          id: user?.pickpackId || '',
        },
        onCompleted: (data) => {
          if (data?.adminUpdateUser != null) {
            setTapToBorrowEnabled(
              data.adminUpdateUser.feature_tap_to_borrow_enabled,
            );
            setTapToBorrowTwoTapFlow(
              data.adminUpdateUser.feature_tap_to_borrow_two_tap_flow,
            );
          }
        },
        onError: (e) => {
          LayoutStoreInstance.setError(e.message.replace(/_/g, ' '));
        },
      });
    } else {
      LayoutStoreInstance.setError(i18n.t('Users:userDoesNotHavePickpackId'));
    }
  }

  async function saveTapToBorrowType() {
    let tapToBorrowTypeToSave = tapToBorrowType;
    let isTwoTapFlow = tapToBorrowTwoTapFlow;

    if (!tapToBorrowTypeToSave) {
      return LayoutStoreInstance.setError(
        i18n.t('Users:invalidTap2BorrowType'),
      );
    }

    if (tapToBorrowTypeToSave === stripeSetupIntentInclPiFallback) {
      toggleTapToBorrow({
        tapToBorrowEnabled,
        tapToBorrowTwoTapFlow: true,
      });

      isTwoTapFlow = true;

      tapToBorrowTypeToSave = TapToBorrowType.StripeSetupIntent;
    } else {
      toggleTapToBorrow({
        tapToBorrowEnabled,
        tapToBorrowTwoTapFlow: false,
      });

      isTwoTapFlow = false;
    }

    if (user?.pickpackId != null) {
      await toggleFeatureTapToBorrowType({
        variables: {
          tap_to_borrow_type: tapToBorrowTypeToSave as TapToBorrowType,
          id: user?.pickpackId || '',
        },
        onCompleted: (data) => {
          if (data?.adminUpdateUser != null) {
            let type = data.adminUpdateUser.tap_to_borrow_type;

            setSavedTapToBorrowType(type);
            setTapToBorrowType(
              type === TapToBorrowType.StripeSetupIntent && isTwoTapFlow
                ? stripeSetupIntentInclPiFallback
                : type,
            );
          }
        },
        onError: (e) => {
          LayoutStoreInstance.setError(e.message.replace(/_/g, ' '));
        },
      });
    } else {
      LayoutStoreInstance.setError(i18n.t('Users:userDoesNotHavePickpackId'));
    }
  }

  useEffect(() => {
    if (
      tapToBorrowEnabled &&
      user?.pickpackId &&
      userClaim === 'imac' &&
      !stripeTerminalLocationFetched
    ) {
      setStripeTerminalLocationFetched(true);
      getStripeTerminalLocation({
        variables: { user_id: user.pickpackId },
      });
    }
  }, [
    tapToBorrowEnabled,
    user?.pickpackId,
    userClaim,
    stripeTerminalLocationFetched,
    getStripeTerminalLocation,
  ]);

  const storeAddress = storeData?.store?.address;
  const formattedStoreAddress = storeAddress
    ? `${storeAddress.streetname || ''} ${storeAddress.streetno || ''}, ${storeAddress.zip || ''} ${storeAddress.city || ''}${storeAddress.state ? `, ${storeAddress.state}` : ''}`.trim()
    : null;

  const storeCountryCode = storeData?.store?.country_code;
  const support = tapToBorrowSupportData?.getTapToBorrowSupportByCountry;

  function getSupportedCountriesForType(
    type: TapToBorrowTypeExpanded | null,
  ): string[] | null {
    if (!support || !type) return null;
    switch (type) {
      case TapToBorrowType.StripePaymentIntent:
        return support.countries_enabled_for_stripe_payment_intent;
      case TapToBorrowType.StripeSetupIntent:
        return support.countries_enabled_for_stripe_setup_intent;
      case stripeSetupIntentInclPiFallback:
        return support.countries_enabled_for_stripe_setup_intent_incl_pi_fallback;
      case TapToBorrowType.StripePaymentIntentAndPay:
        return support.countries_enabled_for_stripe_payment_intent_and_pay;
      case TapToBorrowType.StripePaymentIntentAndPaySavedCard:
        return support.countries_enabled_for_stripe_payment_intent_and_pay_saved_card;
      default:
        return null;
    }
  }

  // Both "tap to borrow & pay" variants send the product portion to the partner's
  // Stripe connected account, so both require a linked account.
  const tapToBorrowTypesRequiringConnectedAccount: string[] = [
    TapToBorrowType.StripePaymentIntentAndPay,
    TapToBorrowType.StripePaymentIntentAndPaySavedCard,
  ];

  // The saved state is inconsistent: an and-pay type is active, but there is no account
  // the product portion could be transferred to, so it stays with Vytal. Saving this
  // combination is rejected by the backend — legacy users can still be in it.
  const isAndPayWithoutConnectedAccount =
    savedTapToBorrowType != null &&
    tapToBorrowTypesRequiringConnectedAccount.includes(savedTapToBorrowType) &&
    !stripeConnectedAccountId;

  const supportedCountries = getSupportedCountriesForType(tapToBorrowType);
  const isTapToBorrowTypeUnsupported =
    storeCountryCode != null &&
    supportedCountries != null &&
    !supportedCountries.includes(storeCountryCode);

  const canAccessUser =
    CanAccessUserData(viewer) ||
    (viewer.role === Role.accountManager && userClaim === 'imac') ||
    (viewer.role === Role.humanResources && IsVytalEmployeeClaim(userClaim));

  if (user == null || !canAccessUser) {
    return null;
  }

  return (
    <div>
      <table className="table">
        <tbody>
          {userClaim != null && IsSelectableClaim(userClaim) && (
            <tr>
              <td>{i18n.t('Users:claim')}</td>
              <td style={{ display: 'flex' }}>
                <p className="mb-0 me-2">{userClaim}</p>
                <SelectClaimModal
                  userClaimIn={userClaim}
                  userUuid={user.id}
                  onUserClaimSaveSuccess={(newClaim) => setUserClaim(newClaim)}
                />
              </td>
            </tr>
          )}
          <tr>
            <td>{i18n.t('Users:username')}</td>
            <td>{user.userName}</td>
          </tr>
          <tr>
            <td>{i18n.t('Users:email')}</td>
            <td>{user.email != null ? user.email : i18n.t('Users:notSet')}</td>
          </tr>
          <tr>
            <td>{i18n.t('Users:loginType')}</td>
            <td>{getLoginType()}</td>
          </tr>
          <tr>
            <td>{i18n.t('Users:name')}</td>
            <td>{user.name != null ? user.name : i18n.t('Users:notSet')}</td>
          </tr>
          <tr>
            <td>{i18n.t('Users:id')}</td>
            <td>{user.id}</td>
          </tr>
          <tr>
            <td>{i18n.t('Users:pickpackId')}</td>
            <td>
              {user.pickpackId != null
                ? user.pickpackId
                : i18n.t('Users:notSet')}
            </td>
          </tr>
          <tr>
            <td>{i18n.t('Users:phoneNumber')}</td>
            <td>
              {user.phoneNumber != null
                ? user.phoneNumber
                : i18n.t('Users:notSet')}
            </td>
          </tr>
          <tr>
            <td>{i18n.t('Users:hasPickpackPaymentSource')}</td>
            <td>
              {user.hasPickpackPayment
                ? i18n.t('Users:yes')
                : i18n.t('Users:no')}
            </td>
          </tr>
          <tr>
            <td>{i18n.t('Users:willBeChargedForOverdue')}</td>
            <td>
              {user.isChargeableForOverdueContainers
                ? i18n.t('Users:yes') + ' '
                : i18n.t('Users:no') + ' '}
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => toggleIsChargeableForOverdue()}
              >
                {`${i18n.t('Users:changeTo')} ${i18n.t('Users:isCharged')} ${
                  user.isChargeableForOverdueContainers
                    ? i18n.t('Users:isNotCharged')
                    : ''
                } ${i18n.t('Users:chargedForOverdueContainers')}`}
              </button>
            </td>
          </tr>
          <tr>
            <td>{i18n.t('Users:checkoutLimit')}</td>
            <td>
              {user.hasManualCheckoutLimit
                ? user.manualCheckoutLimit == null
                  ? i18n.t('Users:unlimited') + ' '
                  : user.manualCheckoutLimit + ' '
                : i18n.t('Users:standardForThisUserType') + ' '}
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => toggleShowSetCheckoutLimitModal()}
              >
                {i18n.t('Users:changeCheckoutLimit')}
              </button>
            </td>
          </tr>
          {user.claims.includes('catering_consumer') && (
            <tr>
              <td>{i18n.t('Users:linkedStore')}</td>
              <td>
                {linkedStore != null && (
                  <a
                    href={`/partner/brand/${linkedStore.brandTextId}/store/${linkedStore.textId}`}
                  >
                    {linkedStore.name}
                  </a>
                )}
                {linkedStore == null && <span>{i18n.t('Users:none')}</span>}
                <button
                  type="button"
                  className="btn btn-link ms-1 p-0"
                  onClick={() => setShowSelectLinkedStoreModal(true)}
                >
                  {i18n.t('Users:selectLinkedStoreBtn')}
                </button>
              </td>
            </tr>
          )}
          {firstCheckoutStore != null && (
            <tr>
              <td>{i18n.t('Users:firstCheckoutStore')}</td>
              <td>
                <a
                  href={`/partner/brand/${firstCheckoutStore.brandTextId}/store/${firstCheckoutStore.textId}`}
                >
                  {firstCheckoutStore.name}
                </a>
              </td>
            </tr>
          )}
          <tr>
            <td>{i18n.t('Users:accountCreationDate')}</td>
            <td>{user.creationDate}</td>
          </tr>
          {user.signupCode && (
            <tr>
              <td>{i18n.t('Users:signupCode')}</td>
              <td>
                {user.signupCode}{' '}
                {user.signupCodeWasUsed
                  ? i18n.t('Users:used')
                  : i18n.t('Users:unused')}
              </td>
            </tr>
          )}
          <tr>
            <td>{i18n.t('Users:associatedOneTimeToken')}</td>
            <td>{oneTimeToken ? oneTimeToken : i18n.t('Users:none')}</td>
          </tr>
          <tr>
            <td>{i18n.t('Users:internalUser')}</td>
            <td>
              {user.isInternal}(
              {user.isInternal ? (
                <>
                  {i18n.t('Users:yes')}{' '}
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    onClick={() => toggleIsInternal()}
                  >
                    {i18n.t('Users:makeNonInternalUser')}
                  </button>
                </>
              ) : (
                <>
                  {i18n.t('Users:no')}{' '}
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    onClick={() => toggleIsInternal()}
                  >
                    {i18n.t('Users:makeInternalUser')}
                  </button>
                </>
              )}
              )
            </td>
          </tr>
          <tr>
            <td>{i18n.t('Users:clientVersion')}</td>
            <td>{user.clientVersion || ''}</td>
          </tr>
          <tr>
            <td>{i18n.t('Users:clientName')}</td>
            <td>{user.clientName || ''}</td>
          </tr>
          <tr>
            <td>{i18n.t('Users:passwordPartnerAppUsers')}</td>
            <td>{user.password || ''}</td>
          </tr>
          {userClaim === 'imac' && (
            <>
              <tr>
                <td>{i18n.t('Users:nextHandoutEnabled')}</td>
                <td>{getNextHandoutEnabled(user)} </td>
              </tr>
              <tr>
                <td>{i18n.t('Users:tapToBorrowEnabled')}</td>
                <td>{getTapToBorrowEnabled(user)} </td>
              </tr>
              <tr>
                <td>{i18n.t('Users:tapToBorrowType')}</td>
                <td>
                  <div className="input-group">
                    <select
                      className="form-control"
                      disabled={
                        updatingTapToBorrowType ||
                        user.pickpackId == null ||
                        (viewer.role !== Role.administrator &&
                          viewer.role !== Role.accountManager) ||
                        !tapToBorrowEnabled
                      }
                      value={tapToBorrowType || ''}
                      onChange={(e) => {
                        // Only set the type if it's not the default/placeholder option
                        if (e.target.value && e.target.value !== 'default') {
                          setTapToBorrowType(e.target.value as TapToBorrowType);
                        }
                      }}
                    >
                      {/* Conditional placeholder option */}
                      {!tapToBorrowType && (
                        <option value="" disabled>
                          {user.pickpackId != null
                            ? i18n.t('Users:selectTypePlaceholder')
                            : i18n.t('Users:requiresLoginTapToBorrow')}
                        </option>
                      )}

                      {[
                        ...(tapToBorrowEnumInfo?.enumValues || []),
                        { name: stripeSetupIntentInclPiFallback }, // Insert two tap flow option
                      ]
                        .sort((a, b) => {
                          return a.name.localeCompare(b.name);
                        })
                        .map((enumInfo: any) => (
                          <option
                            key={enumInfo.name}
                            value={enumInfo.name}
                            disabled={
                              tapToBorrowTypesRequiringConnectedAccount.includes(
                                enumInfo.name,
                              ) && !stripeConnectedAccountId
                            }
                          >
                            {enumInfo.name}
                          </option>
                        ))}
                    </select>{' '}
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={
                        updatingTapToBorrowType ||
                        !tapToBorrowType ||
                        isTapToBorrowTypeUnsupported ||
                        (tapToBorrowTypesRequiringConnectedAccount.includes(
                          tapToBorrowType,
                        ) &&
                          !stripeConnectedAccountId)
                      }
                      onClick={() => saveTapToBorrowType()}
                    >
                      {i18n.t('Common:save')}
                    </button>
                  </div>
                  {isTapToBorrowTypeUnsupported && (
                    <p className="tw:mt-4 tw:text-amber-600 tw:text-sm tw:font-semibold">
                      {tapToBorrowType} is not supported for store country "
                      {storeCountryCode}". Supported countries:{' '}
                      {supportedCountries?.join(', ')}
                    </p>
                  )}
                  {isAndPayWithoutConnectedAccount && (
                    <p className="tw:mt-4 tw:text-red-600 tw:text-sm tw:font-semibold">
                      ⚠️ {savedTapToBorrowType} is active without a linked
                      Stripe connected account: the money for the product within
                      the tap2borrow flow is passed to Vytal's Stripe account
                      instead of the partner's. Link the connected account
                      below.
                    </p>
                  )}
                  {tapToBorrowEnabled &&
                    !stripeConnectedAccountId &&
                    !isAndPayWithoutConnectedAccount && (
                      <p className="tw:mt-4 tw:text-gray-500 tw:text-sm">
                        {TapToBorrowType.StripePaymentIntentAndPay} and{' '}
                        {TapToBorrowType.StripePaymentIntentAndPaySavedCard}{' '}
                        require a linked Stripe connected account (see below).
                      </p>
                    )}
                </td>
              </tr>
              <tr>
                <td>Stripe Connected Account</td>
                <td>
                  <div className="input-group">
                    <input
                      className="form-control"
                      type="text"
                      placeholder="acct_..."
                      disabled={
                        updatingConnectedAccount ||
                        user.pickpackId == null ||
                        (viewer.role !== Role.administrator &&
                          viewer.role !== Role.accountManager)
                      }
                      value={connectedAccountInput}
                      onChange={(e) => setConnectedAccountInput(e.target.value)}
                    />{' '}
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={
                        updatingConnectedAccount ||
                        user.pickpackId == null ||
                        connectedAccountInput.trim() ===
                          (stripeConnectedAccountId || '')
                      }
                      onClick={() => saveStripeConnectedAccount()}
                    >
                      {updatingConnectedAccount ? (
                        <ClassicLoadingSpinner />
                      ) : (
                        i18n.t('Common:save')
                      )}
                    </button>
                  </div>
                  <p className="tw:mt-4 tw:text-gray-500 tw:text-sm">
                    Receives the product portion of "
                    {TapToBorrowType.StripePaymentIntentAndPay}" and "
                    {TapToBorrowType.StripePaymentIntentAndPaySavedCard}"
                    payments. The account is validated against Stripe on save;
                    leave empty to remove the link.
                  </p>
                </td>
              </tr>
              {tapToBorrowEnabled && user.pickpackId != null && (
                <>
                  {loadingStripeTerminalLocation && (
                    <tr>
                      <td>Stripe Terminal Location</td>
                      <td>
                        <ClassicLoadingSpinner />
                      </td>
                    </tr>
                  )}
                  {stripeTerminalLocationError && (
                    <tr>
                      <td>Stripe Terminal Location</td>
                      <td className="tw:text-red-500">
                        Error: {stripeTerminalLocationError.message}
                      </td>
                    </tr>
                  )}
                  {(() => {
                    const loc =
                      stripeTerminalLocationData?.getStripeTerminalLocation;
                    if (!loc) return null;
                    return (
                      <>
                        <tr>
                          <td>Stripe Terminal Location</td>
                          <td>
                            {loc.display_name} ({loc.id})
                          </td>
                        </tr>
                        <tr>
                          <td>Terminal Address</td>
                          <td>
                            {loc.address.line1}, {loc.address.postal_code}{' '}
                            {loc.address.city}, {loc.address.country}
                          </td>
                        </tr>
                        <tr>
                          <td>Terminal Livemode</td>
                          <td>{loc.livemode ? 'Yes' : 'No'}</td>
                        </tr>
                        {loc.deleted != null && (
                          <tr>
                            <td>Terminal Deleted</td>
                            <td>{loc.deleted ? 'Yes' : 'No'}</td>
                          </tr>
                        )}
                      </>
                    );
                  })()}
                  {!loadingStripeTerminalLocation &&
                    !stripeTerminalLocationError &&
                    !stripeTerminalLocationData?.getStripeTerminalLocation &&
                    stripeTerminalLocationFetched && (
                      <tr>
                        <td>Stripe Terminal Location</td>
                        <td>Not found</td>
                      </tr>
                    )}
                </>
              )}
              <tr>
                <td>{i18n.t('Users:containerStationLink')}: </td>
                <td>
                  {user.linkedContainerStation?.name || '-'}{' '}
                  {!loading && !linkingUserToContainerStation && (
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      onClick={() =>
                        setLinkUserToContainerStationModalOpen(true)
                      }
                    >
                      {i18n.t('Users:linkToContainerStation')}
                    </button>
                  )}
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>
      {linkContainerStationModal()}
      <CustomModal
        isOpen={showSelectLinkedStoreModal}
        setIsOpen={setShowSelectLinkedStoreModal}
        acceptButtonAction={() => {
          console.log('linking store' + selectedStoreTextId);
          linkCustomerCateringUser({
            variables: {
              user_uuid: user.id,
              store_text_id: selectedStoreTextId,
            },
          });
        }}
        acceptButtonDisabled={isNullOrWhitespace(selectedStoreTextId)}
        acceptButtonText={i18n.t('Users:link')}
        body={
          <StoreSearch
            setStoreInfo={(arg, id, name, brandId) => {
              setSelectedStoreTextId(id);
              setSelectedStoreName(name);
              setSelectedStoreBrandId(brandId || '');
            }}
          />
        }
        header={i18n.t('Users:selectLinkedStoreModal')}
        acceptButtonIcon={<FaSave />}
      />
      <CustomModal
        isOpen={showEnableTapToBorrowModal}
        setIsOpen={setShowEnableTapToBorrowModal}
        header="Enable Tap to Borrow"
        acceptButtonText="Enable tap to borrow"
        acceptButtonIcon={null}
        acceptButtonDisabled={storeLoading}
        acceptButtonAction={() => {
          toggleTapToBorrow({
            tapToBorrowEnabled: true,
            tapToBorrowTwoTapFlow,
          });
          setShowEnableTapToBorrowModal(false);
        }}
        body={
          <div>
            <p>
              Please confirm the{' '}
              {colugoStoreId ? (
                <a
                  href={`/pickpack/stores/${colugoStoreId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw:underline tw:text-blue-600"
                >
                  store
                </a>
              ) : (
                'store'
              )}{' '}
              details before enabling tap to borrow for this user:
            </p>
            <br />
            {storeLoading ? (
              <ClassicLoadingSpinner />
            ) : (
              <>
                <p>
                  <strong>Country:</strong>{' '}
                  {storeData?.store?.country_code || 'N/A'}
                </p>
                <p>
                  <strong>Address:</strong> {formattedStoreAddress || 'N/A'}
                </p>
              </>
            )}
            <p className="tw:mt-4 tw:text-amber-600 tw:font-semibold">
              If the store country changes after tap to borrow feature
              activation, please delete the associated partner app user and
              create a new one.
            </p>
          </div>
        }
      />
    </div>
  );
}
